import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, isValidUser } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";

const emptySignup = { name: "", email: "", phone: "", city: "", otp: "", password: "", confirmPassword: "", terms: false, referralCode: "" };

const destinationFor = (user) => {
  if (user.role === "admin") return "/admin";
  if (user.role === "mentor") return "/mentor";
  if (user.role === "counselor") return "/counselor";
  return "/dashboard";
};

const AuthShell = ({ children, title, subtitle, panelMode, setView, showBack, onBack }) => {
  const isSplit = panelMode === "login" || panelMode === "signup";
  
  return (
    <div className="min-h-[calc(100dvh-46px)] sm:min-h-screen flex flex-col items-center justify-start sm:justify-center bg-[#070606] py-3 sm:py-8 px-3 sm:px-6 lg:p-10 text-white font-outfit relative overflow-y-auto" data-testid="admin-login-page">
      {/* Ambient background glows for deep atmosphere */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(255,106,61,0.08),transparent_70%)] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(255,160,80,0.06),transparent_70%)] blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:linear-gradient(rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:64px_64px]" />

      <div className="w-full max-w-[1040px] my-auto bg-[#120E0C]/95 backdrop-blur-2xl rounded-2xl sm:rounded-3xl border border-white/[0.08] flex flex-col lg:flex-row overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.9),0_0_40px_rgba(255,106,61,0.05)] relative z-10 animate-[authIn_.45s_ease-out]">
        
        {/* Mobile-only Hero Header Video Banner */}
        {isSplit && (
          <div className="block lg:hidden w-full p-3 sm:p-4 pb-0">
            <div className="w-full h-36 sm:h-48 relative rounded-xl sm:rounded-2xl overflow-hidden bg-[#0A0706] border border-[#FF6A3D]/20 shadow-lg flex flex-col justify-between p-3 sm:p-4">
              {/* Internal ambient radial glow */}
              <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-[radial-gradient(circle,rgba(255,106,61,0.3),transparent_65%)] blur-xl pointer-events-none" />
              
              {/* Video Animation */}
              <video
                src="/cyborg-login-animation.mp4"
                poster="/login-cyborg.webp"
                preload="auto"
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover object-[center_18%] sm:object-[55%_25%] opacity-95 mix-blend-screen pointer-events-none"
              />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0706] via-[#0A0706]/30 to-transparent opacity-90 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0706]/40 via-transparent to-transparent opacity-100 pointer-events-none" />

              {/* Top Tech Badge */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 border border-white/10 backdrop-blur-md">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[9px] font-mono tracking-widest text-zinc-300 uppercase">DRISHTI AI</span>
                </div>
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 border border-white/10 backdrop-blur-md text-[9px] font-mono text-zinc-300">
                  <span className="text-[#FF6A3D]">⚡</span> Terminal
                </div>
              </div>

              {/* Bottom Mini Badge */}
              <div className="relative z-10 backdrop-blur-md bg-black/60 border border-white/10 rounded-lg px-2.5 py-1 flex items-center justify-between">
                <span className="text-[10px] font-bold text-white flex items-center gap-1">
                  Jamadhan Intelligence
                </span>
                <span className="text-[9px] font-mono text-emerald-400">Institutional</span>
              </div>
            </div>
          </div>
        )}

        {/* Left Panel - Form */}
        <div className={`w-full ${isSplit ? 'lg:w-[48%]' : 'max-w-xl mx-auto'} p-5 sm:p-8 lg:p-12 flex flex-col justify-center relative`}>
          {showBack && (
            <button type="button" onClick={onBack} className="absolute left-5 top-5 sm:left-8 sm:top-8 flex items-center gap-2 text-xs sm:text-sm font-semibold text-zinc-300 transition hover:text-white z-20 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:border-white/20">
              <ArrowLeft className="h-3.5 w-3.5 text-[#FF6A3D]" /> Back
            </button>
          )}

          <div className="flex flex-col items-center mb-5 text-center">
            {/* Jamadhan Logo */}
            <div className="relative mb-2.5 flex items-center justify-center">
              <div className="absolute inset-0 rounded-2xl bg-[#FF6A3D]/20 blur-md" />
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-contain border border-white/10 shadow-lg bg-black/40 p-1" />
            </div>
            
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-white">{title}</h1>
            <p className="text-xs text-[#9E9E9E] mt-1 max-w-[280px] leading-relaxed">{subtitle}</p>
          </div>

          {isSplit && (
            <div className="flex items-center bg-[#1A1412] rounded-full p-1 mb-5 border border-white/[0.08]">
              <button 
                type="button" 
                onClick={() => setView('login')}
                className={`flex-1 text-xs font-bold uppercase tracking-wider py-2.5 rounded-full transition-all duration-300 ${panelMode === 'login' ? 'bg-gradient-to-r from-[#FF6A3D] to-[#FFA756] text-black shadow-[0_4px_16px_rgba(255,106,61,0.35)]' : 'text-[#8A8A8A] hover:text-white'}`}
              >
                Log In
              </button>
              <button 
                type="button" 
                onClick={() => setView('signup')}
                className={`flex-1 text-xs font-bold uppercase tracking-wider py-2.5 rounded-full transition-all duration-300 ${panelMode === 'signup' ? 'bg-gradient-to-r from-[#FF6A3D] to-[#FFA756] text-black shadow-[0_4px_16px_rgba(255,106,61,0.35)]' : 'text-[#8A8A8A] hover:text-white'}`}
              >
                Sign Up
              </button>
            </div>
          )}

          <div className="flex-1 flex flex-col justify-center">
            {children}
          </div>
        </div>

        {/* Right Panel - Cyborg Showcase for Desktop (>= lg) */}
        {(isSplit || !isSplit) && (
          <div className={`hidden lg:flex ${isSplit ? 'lg:w-[52%]' : 'lg:w-[52%]'} p-4 relative flex-col justify-between`}>
            <div className="w-full h-full min-h-[580px] relative rounded-2xl overflow-hidden bg-[#0A0706] border border-[#FF6A3D]/20 shadow-2xl flex flex-col justify-between p-6">
              
              {/* Internal ambient radial glow */}
              <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-[radial-gradient(circle,rgba(255,106,61,0.25),transparent_65%)] blur-2xl pointer-events-none" />
              
              {/* Cyborg Visual Animation */}
              <video
                src="/cyborg-login-animation.mp4"
                poster="/login-cyborg.webp"
                preload="auto"
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover object-[55%_25%] opacity-95 mix-blend-screen pointer-events-none"
              />
              
              {/* Directional fades for seamless border integration */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0706] via-transparent to-transparent opacity-80 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0706]/60 via-transparent to-transparent opacity-100 pointer-events-none" />

              {/* Top Tech Badge */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur-md">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] font-mono tracking-widest text-zinc-300 uppercase">DRISHTI AI</span>
                </div>
              </div>

              {/* Bottom Integrated Feature Card */}
              <div className="relative z-10 backdrop-blur-xl bg-black/60 border border-white/10 rounded-xl p-5 shadow-2xl">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                    <span className="text-[#FF6A3D]">⚡</span> Jamadhan Intelligence Terminal
                  </p>
                  <span className="text-[10px] font-mono text-zinc-400">Institutional Access</span>
                </div>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Real-time pattern recognition, multi-timeframe Drishti AI alerts, and verified high-probability market setups.
                </p>
              </div>

            </div>
          </div>
        )}

      </div>
      <style>{`
        @keyframes authIn{from{opacity:0;transform:translateY(24px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
      `}</style>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5 mb-4 relative z-10">
    <Label className="text-xs font-medium text-[#D1D1D1]">{label}</Label>
    {children}
  </div>
);

const baseInput = "h-11 rounded-xl border border-white/[0.08] bg-[#171311] text-white placeholder:text-[#666666] text-sm transition-all focus:border-[#FF6A3D] focus:ring-2 focus:ring-[#FF6A3D]/20 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)]";
const primaryBtn = "h-11 w-full rounded-xl bg-gradient-to-r from-[#FF6A3D] via-[#FF7D45] to-[#FFA756] font-bold text-black text-sm uppercase tracking-wider shadow-[0_8px_24px_rgba(255,106,61,0.3)] transition-all hover:brightness-110 hover:shadow-[0_12px_30px_rgba(255,106,61,0.45)] active:scale-[0.99]";

const AdminLoginPage = () => {
  const { adminLogin, startSignup, verifySignup, completeSignup, requestPasswordReset, resetPassword, user } = useAuth();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const isSignupMode = searchParams.get("mode") === "signup" || Boolean(searchParams.get("ref"));
  const [view, setView] = useState(isSignupMode ? "signup" : "login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "", remember: false });
  const [signup, setSignup] = useState(emptySignup);
  const [reset, setReset] = useState({ email: "", otp: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    if (isValidUser(user)) navigate(destinationFor(user), { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    // Preserve referral code from URL in session for later checkout
    const urlRef = (searchParams.get("ref") || "").trim().toUpperCase();
    if (urlRef) {
      try {
        localStorage.setItem("jamadhan_referral_code", urlRef);
        sessionStorage.setItem("jamadhan_referral_code", urlRef);
      } catch (_) {}
    }
  }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const loggedIn = await adminLogin(loginForm.email, loginForm.password);
      toast.success(`Welcome, ${loggedIn.name}!`);
      navigate(destinationFor(loggedIn), { replace: true });
    } catch (error) {
      const msg = error?.response?.data?.detail || "Invalid credentials";
      setLoginError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const sendSignupOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await startSignup(signup);
      toast.success("Verification code sent to your email");
      setView("otp");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not send verification code");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifySignup(signup.email, signup.otp);
      toast.success("Email verified");
      setView("password");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const finishSignup = async (e) => {
    e.preventDefault();
    if (signup.password !== signup.confirmPassword) return toast.error("Passwords do not match");
    if (!signup.terms) return toast.error("Please agree to the terms");
    setLoading(true);
    try {
      const newUser = await completeSignup(signup.email, signup.password);
      setCreatedUser(newUser);
      setView("done");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  const sendResetOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestPasswordReset(reset.email);
      toast.success("Reset code sent to your email");
      setView("reset-code");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not send reset code");
    } finally {
      setLoading(false);
    }
  };

  const finishReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetPassword(reset.email, reset.otp, reset.password);
      toast.success("Password reset. Please sign in.");
      setView("login");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (view === "signup") {
    return (
      <AuthShell title="Create Account" subtitle="Enter your details to create your account" panelMode="signup" setView={setView}>
        <form onSubmit={sendSignupOtp} className="space-y-0 relative z-10">
          <Field label="Full Name*"><Input value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} className={baseInput} placeholder="Enter your full name..." required /></Field>
          <Field label="Email*"><Input type="email" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} className={baseInput} placeholder="Enter your email..." required /></Field>
          <div className="flex flex-col sm:flex-row gap-0 sm:gap-4">
             <div className="w-full sm:w-1/2"><Field label="City*"><Input value={signup.city} onChange={(e) => setSignup({ ...signup, city: e.target.value })} className={baseInput} placeholder="City..." required /></Field></div>
             <div className="w-full sm:w-1/2"><Field label="Mobile*"><Input value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} className={baseInput} placeholder="Mobile number..." required /></Field></div>
          </div>
          
          <Button disabled={loading} className={`${primaryBtn} mt-2`}>{loading ? "Sending..." : "Sign Up"}</Button>
        </form>
      </AuthShell>
    );
  }

  if (view === "otp" || view === "reset-code") {
    const isReset = view === "reset-code";
    return (
      <AuthShell title="Check your email" subtitle={`We sent a 6-digit code to ${isReset ? reset.email : signup.email}`} panelMode="otp" setView={setView} showBack onBack={() => setView(isReset ? "forgot" : "signup")}>
        <form onSubmit={isReset ? finishReset : verifyCode} className="space-y-6 relative z-10">
          <div className="relative">
            <Input
              value={isReset ? reset.otp : signup.otp}
              maxLength={6}
              inputMode="numeric"
              onChange={(e) => isReset ? setReset({ ...reset, otp: e.target.value.replace(/\D/g, "") }) : setSignup({ ...signup, otp: e.target.value.replace(/\D/g, "") })}
              className="absolute inset-0 z-10 h-14 cursor-text opacity-0"
              autoFocus
            />
            <div className="grid grid-cols-6 gap-2 sm:gap-3">
              {Array.from({ length: 6 }).map((_, i) => {
                const digit = (isReset ? reset.otp : signup.otp)[i] || "";
                const activeIndex = (isReset ? reset.otp : signup.otp).length;
                return (
                  <div
                    key={i}
                    className={`flex h-12 sm:h-14 items-center justify-center rounded-xl border text-xl font-bold transition-all duration-300 ${
                      digit
                        ? "scale-[1.03] border-[#FF6A3D] bg-[#FF6A3D]/10 text-white shadow-[0_0_20px_rgba(255,106,61,.4)]"
                        : i === activeIndex
                          ? "border-[#FF6A3D]/70 bg-[#171412] shadow-[0_0_15px_rgba(255,106,61,.2)]"
                          : "border-white/10 bg-[#171412]"
                    }`}
                  >
                    {digit}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-center text-xs text-[#8A8A8A]">Code expires in 10 minutes. Check your spam folder if needed.</p>
          {isReset && <Field label="New password*"><Input type="password" value={reset.password} onChange={(e) => setReset({ ...reset, password: e.target.value })} className={baseInput} required /></Field>}
          <Button disabled={loading} className={primaryBtn}>{loading ? "Checking..." : isReset ? "Reset password" : "Verify & continue"}</Button>
          <Button type="button" variant="outline" onClick={isReset ? sendResetOtp : sendSignupOtp} className="h-11 w-full rounded-lg border-white/10 bg-transparent text-[#B0B0B0] hover:bg-white/5 hover:text-white transition-colors">Resend code</Button>
        </form>
      </AuthShell>
    );
  }

  if (view === "password") {
    const strength = signup.password.length >= 10 ? "Strong strength" : signup.password.length >= 8 ? "Good strength" : "Use at least 8 characters";
    return (
      <AuthShell title="Set your password" subtitle="Choose a strong password to secure your account" panelMode="password" setView={setView} showBack onBack={() => setView("otp")}>
        <form onSubmit={finishSignup} className="space-y-4 relative z-10">
          <Field label="Password*">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={signup.password}
                onChange={(e) => setSignup({ ...signup, password: e.target.value })}
                className={`${baseInput} pr-11`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#8A8A8A] hover:text-white transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Eye className="h-4 w-4 text-[#FF6A3D]" /> : <EyeOff className="h-4 w-4 text-[#71717A]" />}
              </button>
            </div>
          </Field>
          <div className="grid grid-cols-4 gap-1">{[0, 1, 2, 3].map((n) => <span key={n} className={`h-1 rounded-full ${signup.password.length > n * 2 + 3 ? "bg-[#FF6A3D]" : "bg-white/10"}`} />)}</div>
          <p className="text-xs text-[#FF6A3D]">{strength}</p>
          
          <Field label="Confirm password*">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={signup.confirmPassword}
                onChange={(e) => setSignup({ ...signup, confirmPassword: e.target.value })}
                className={`${baseInput} pr-11`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#8A8A8A] hover:text-white transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <Eye className="h-4 w-4 text-[#FF6A3D]" /> : <EyeOff className="h-4 w-4 text-[#71717A]" />}
              </button>
            </div>
          </Field>
          
          <label className="flex items-start gap-3 text-xs sm:text-sm text-zinc-300 cursor-pointer mt-2.5 select-none">
             <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 mt-0.5 transition-colors ${signup.terms ? 'bg-[#FF6A3D] border-[#FF6A3D]' : 'border-white/25 bg-black/50'}`}>
                {signup.terms && <Check className="w-3 h-3 text-white stroke-[3]" />}
             </div>
             <input type="checkbox" checked={signup.terms} onChange={(e) => setSignup({ ...signup, terms: e.target.checked })} className="hidden" /> 
             <span>I agree to the <span className="text-[#FF6A3D] font-medium hover:underline">Terms of Service</span> and <span className="text-[#FF6A3D] font-medium hover:underline">Privacy Policy</span></span>
          </label>
          <Button disabled={loading} className={`${primaryBtn} mt-6`}>{loading ? "Creating..." : "Create my account"}</Button>
        </form>
      </AuthShell>
    );
  }

  if (view === "done") {
    return (
      <AuthShell title="You're all set!" subtitle="Your account has been created successfully. Welcome aboard!" panelMode="done" setView={setView}>
        <div className="space-y-5 relative z-10">
          <div className="rounded-xl border border-white/10 bg-[#171412] p-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#FF6A3D] to-[#FFC875] font-bold text-black text-lg">{createdUser?.name?.slice(0, 2)?.toUpperCase() || "JD"}</div>
              <div className="min-w-0 flex-1"><p className="truncate font-bold text-white text-base">{createdUser?.name}</p><p className="truncate text-xs text-[#8A8A8A] mt-0.5">{createdUser?.email}</p></div>
              <span className="rounded-full border border-[#FF6A3D]/30 bg-[#FF6A3D]/10 px-3 py-1 text-[10px] font-bold text-[#FF6A3D] uppercase tracking-wider">Verified</span>
            </div>
            <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 gap-3 text-xs text-[#A1A1AA]">
               <div className="flex flex-col"><span className="text-[#5A5A5A] mb-1">City</span><span>{createdUser?.city}</span></div>
               <div className="flex flex-col"><span className="text-[#5A5A5A] mb-1">Phone</span><span>{createdUser?.phone}</span></div>
            </div>
          </div>
          <Button onClick={() => navigate(destinationFor(createdUser), { replace: true })} className={primaryBtn}>Go to dashboard</Button>
        </div>
      </AuthShell>
    );
  }

  if (view === "forgot") {
    return (
      <AuthShell title="Reset password" subtitle="We will send a reset code to your email" panelMode="forgot" setView={setView} showBack onBack={() => setView("login")}>
        <form onSubmit={sendResetOtp} className="space-y-4 relative z-10">
          <Field label="Email or Phone*"><Input value={reset.email} onChange={(e) => setReset({ ...reset, email: e.target.value })} className={baseInput} placeholder="Enter your email or phone..." required /></Field>
          <Button disabled={loading} className={`${primaryBtn} mt-4`}>{loading ? "Sending..." : "Send reset code"}</Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Welcome Back" subtitle="Enter your email and password to access your account" panelMode="login" setView={setView}>
      <form onSubmit={submitLogin} className="space-y-0 relative z-10">
        <Field label="Email*">
           <Input value={loginForm.email} onChange={(e) => { setLoginForm({ ...loginForm, email: e.target.value }); setLoginError(""); }} className={baseInput} placeholder="Enter Your Email..." required data-testid="admin-email-input" />
        </Field>
        <Field label="Password*">
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={loginForm.password}
              onChange={(e) => { setLoginForm({ ...loginForm, password: e.target.value }); setLoginError(""); }}
              className={`${baseInput} pr-11`}
              placeholder="Enter Your Password..."
              required
              data-testid="admin-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#8A8A8A] hover:text-white transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <Eye className="h-4 w-4 text-[#FF6A3D]" /> : <EyeOff className="h-4 w-4 text-[#71717A]" />}
            </button>
          </div>
        </Field>

        {/* Contextual error hint — highlights Forgot Password when no password is set */}
        {loginError && (
          <div className="mt-3 rounded-xl border border-[#FF6A3D]/30 bg-[#FF6A3D]/[0.07] px-4 py-3 flex items-start gap-2.5">
            <span className="text-[#FF6A3D] text-base mt-0.5 shrink-0">⚠</span>
            <div className="text-xs text-[#FFBFA0] leading-relaxed">
              {loginError}
              {loginError.toLowerCase().includes("forgot password") && (
                <button
                  type="button"
                  onClick={() => { setReset({ ...reset, email: loginForm.email }); setView("forgot"); }}
                  className="ml-1 font-bold text-[#FF6A3D] underline underline-offset-2 hover:text-[#FFA756] transition-colors"
                >
                  Click here to set one →
                </button>
              )}
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mt-4 mb-6 select-none">
          <label className="flex items-center gap-2.5 text-xs sm:text-sm font-medium text-zinc-200 hover:text-white cursor-pointer group transition-colors">
             <div className={`w-4 h-4 rounded-[4px] border flex items-center justify-center transition-all ${loginForm.remember ? 'bg-[#FF6A3D] border-[#FF6A3D] shadow-[0_0_10px_rgba(255,106,61,0.45)]' : 'border-white/30 bg-black/60 group-hover:border-white/50'}`}>
               {loginForm.remember && <Check className="w-3 h-3 text-white stroke-[3]" />}
             </div>
             <input type="checkbox" className="hidden" checked={loginForm.remember} onChange={(e) => setLoginForm({ ...loginForm, remember: e.target.checked })} />
             <span>Remember me</span>
          </label>
          <button type="button" onClick={() => { setReset({ ...reset, email: loginForm.email }); setView("forgot"); }} className="text-xs sm:text-sm font-semibold text-[#FFA756] hover:text-[#FF6A3D] hover:underline transition-colors">
            Forgot Password?
          </button>
        </div>
        
        <Button disabled={loading} className={primaryBtn} data-testid="admin-login-submit">{loading ? "Signing in..." : "Sign in"}</Button>
        
        <Link 
          to="/" 
          className="mt-6 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-zinc-300 hover:text-white transition-all py-2.5 px-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-[#FF6A3D]/40 group"
        >
          <ArrowLeft className="h-4 w-4 text-[#FF6A3D] group-hover:-translate-x-1 transition-transform" /> 
          <span>Back to Home</span>
        </Link>
      </form>
    </AuthShell>
  );
};

export default AdminLoginPage;
